import InfoTooltip from "../../../../../shared/components/InfoTooltip";
import Button from "../../../../../shared/components/Button";
import Spinner from "../../../../../shared/components/Spinner";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import { formatDateTimeLabel } from "../../../../../shared/utils/dateTime";
import {
  AuthUser,
  CategoryChangeRequest,
  USER_CATEGORY_VALUES,
} from "../../../../auth/shared/types/auth";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type ProfileCategoryRequestTabProps = {
  user: AuthUser;
  dashboardTexts: typeof siteTexts.profile.user.dashboard;
  info: typeof siteTexts.common.info;
  open: boolean;
  requestedCategoryDraft: AuthUser["category"] | "";
  categoryRequestReasonDraft: string;
  categoryRequestObservationDraft: string;
  pendingCategoryRequest?: CategoryChangeRequest | null;
  categoryRequestLoading: boolean;
  categoryRequestSaving: boolean;
  categoryRequestError: string;
  categoryRequestSuccess: string;
  categoryChangeRequests: CategoryChangeRequest[];
  sortedCategoryChangeRequests: CategoryChangeRequest[];
  onToggle: () => void;
  onRequestedCategoryChange: (value: AuthUser["category"] | "") => void;
  onReasonChange: (value: string) => void;
  onObservationChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  localizedCategoryLabel: (value: AuthUser["category"] | string) => string;
};

function categoryChangeRequestStatusLabel(
  status: CategoryChangeRequest["status"],
) {
  const labels = siteTexts.profile.user.categoryRequest.statuses;
  if (status === "approved") return labels.approved;
  if (status === "rejected") return labels.rejected;
  return labels.pending;
}

export default function ProfileCategoryRequestTab({
  user,
  dashboardTexts,
  info,
  open,
  requestedCategoryDraft,
  categoryRequestReasonDraft,
  categoryRequestObservationDraft,
  pendingCategoryRequest,
  categoryRequestLoading,
  categoryRequestSaving,
  categoryRequestError,
  categoryRequestSuccess,
  categoryChangeRequests,
  sortedCategoryChangeRequests,
  onToggle,
  onRequestedCategoryChange,
  onReasonChange,
  onObservationChange,
  onSubmit,
  onCancel,
  localizedCategoryLabel,
}: ProfileCategoryRequestTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-category-request"
      icon="swap_horiz"
      title={siteTexts.profile.user.categoryRequest.title}
      subtitle={dashboardTexts.requestReviewHint}
      open={open}
      onToggle={onToggle}
    >
      <section className="user-profile-category-panel">
        <UserProfileSectionHeader
          className="user-profile-category-panel-header"
          icon="swap_horiz"
          title={siteTexts.profile.user.categoryRequest.title}
          description={dashboardTexts.requestReviewHint}
          actions={
            <div className="user-profile-category-alert">
              <span className="material-symbols-outlined" aria-hidden="true">
                info
              </span>
              {dashboardTexts.requestApprovalHint}
            </div>
          }
        />

        <div className="user-profile-category-fields">
          <div className="user-profile-category-current-card">
            <span className="material-symbols-outlined" aria-hidden="true">
              leaderboard
            </span>
            <div>
              <small className="info-label">
                {dashboardTexts.stats.category}
                <InfoTooltip content={info.currentCategory} />
              </small>
              <strong>{localizedCategoryLabel(user.category || "")}</strong>
            </div>
          </div>

          <label className="field">
            <span className="info-label">
              {siteTexts.profile.user.categoryRequest.newCategory}
              <InfoTooltip content={info.categoryChange} />
            </span>
            <select
              value={requestedCategoryDraft}
              onChange={(event) =>
                onRequestedCategoryChange(
                  event.target.value as AuthUser["category"] | "",
                )
              }
              disabled={Boolean(pendingCategoryRequest)}
            >
              <option value="">
                {siteTexts.profile.user.categoryRequest.selectCategory}
              </option>
              {USER_CATEGORY_VALUES.filter(Boolean)
                .filter((item) => item !== user.category)
                .map((item) => (
                  <option
                    key={`profile-request-category-inline-${item}`}
                    value={item}
                  >
                    {localizedCategoryLabel(item)}
                  </option>
                ))}
            </select>
          </label>

          <label className="field">
            <span>{dashboardTexts.requestReason}</span>
            <select
              value={categoryRequestReasonDraft}
              onChange={(event) => onReasonChange(event.target.value)}
              disabled={Boolean(pendingCategoryRequest)}
            >
              <option value="">{dashboardTexts.selectRequestReason}</option>
              {dashboardTexts.categoryReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field user-profile-category-observation">
          <span>{dashboardTexts.observationOptional}</span>
          <textarea
            value={categoryRequestObservationDraft}
            onChange={(event) => onObservationChange(event.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={dashboardTexts.observationPlaceholder}
            disabled={Boolean(pendingCategoryRequest)}
          />
        </label>

        {categoryRequestLoading && (
          <Spinner label={siteTexts.profile.user.loadingCategoryRequests} />
        )}
        {pendingCategoryRequest && (
          <div className="user-profile-category-panel-note">
            <span className="material-symbols-outlined" aria-hidden="true">
              info
            </span>
            Você só pode ter uma solicitação ativa. Aguarde a análise da equipe.
          </div>
        )}
        {categoryRequestError && (
          <p className="status-message">{categoryRequestError}</p>
        )}
        {categoryRequestSuccess && (
          <p className="status-message">{categoryRequestSuccess}</p>
        )}

        <section className="user-profile-category-history">
          <div className="user-profile-category-history-header">
            <div>
              <h3>{siteTexts.profile.user.categoryRequest.historyTitle}</h3>
            </div>
          </div>
          {categoryChangeRequests.length === 0 ? (
            <p className="user-profile-category-history-empty">
              {siteTexts.profile.user.categoryRequest.historyEmpty}
            </p>
          ) : (
            <div className="user-profile-category-history-list">
              {sortedCategoryChangeRequests.map((request) => (
                <article
                  key={`profile-category-history-${request.id}`}
                  className={`user-profile-category-history-card user-profile-category-history-card--${request.status}`}
                >
                  <div className="user-profile-category-history-main">
                    <span
                      className={`user-profile-category-history-status user-profile-category-history-status--${request.status}`}
                    >
                      {categoryChangeRequestStatusLabel(request.status)}
                    </span>
                    <strong>
                      {localizedCategoryLabel(request.currentCategory)}{" "}
                      {siteTexts.profile.user.categoryRequest.historyTo}{" "}
                      {localizedCategoryLabel(request.requestedCategory)}
                    </strong>
                    <small>
                      {siteTexts.profile.user.categoryRequest.requestedAt}{" "}
                      {formatDateTimeLabel(request.createdAt)}
                    </small>
                  </div>
                  {request.reason && (
                    <p className="user-profile-category-history-reason">
                      <span>
                        {siteTexts.profile.user.categoryRequest.reasonLabel}:
                      </span>{" "}
                      {request.reason}
                    </p>
                  )}
                  {request.status !== "pending" && (
                    <div className="user-profile-category-history-resolution">
                      {request.resolvedAt && (
                        <small>
                          {siteTexts.profile.user.categoryRequest.resolvedAt}{" "}
                          {formatDateTimeLabel(request.resolvedAt)}
                        </small>
                      )}
                      {request.adminNote ? (
                        <p>
                          <span>
                            {siteTexts.profile.user.categoryRequest.adminResponse}
                            :
                          </span>{" "}
                          {request.adminNote}
                        </p>
                      ) : (
                        request.status === "rejected" && (
                          <p>
                            {
                              siteTexts.profile.user.categoryRequest
                                .noAdminResponse
                            }
                          </p>
                        )
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        <div className="user-profile-actions">
          <Button
            type="button"
            variant="pill"
            onClick={onCancel}
            disabled={categoryRequestSaving}
          >
            {siteTexts.profile.user.cancel}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onSubmit}
            disabled={
              categoryRequestSaving ||
              categoryRequestLoading ||
              Boolean(pendingCategoryRequest)
            }
          >
            {categoryRequestSaving
              ? siteTexts.profile.user.categoryRequest.sending
              : siteTexts.profile.user.categoryRequest.submit}
          </Button>
        </div>
      </section>
    </ProfileAccordionSection>
  );
}
